// components/ReactPlayerAdapter.tsx
'use client'

import dynamic from 'next/dynamic'
import type { PlayerProps } from 'next-video'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import Image from 'next/image'

// Load client-only to avoid SSR hydration issues
const ReactPlayer = dynamic(() => import('react-player'), { 
  ssr: false,
  loading: () => <div style={{ width: '100%', aspectRatio: '16 / 9', background: '#000' }} />
})

// Accept our visibility + flags via data-* so TS never narrows props incorrectly
type AdapterProps = PlayerProps & {
  ['data-playing']?: boolean
  ['data-loop']?: boolean
  ['data-muted']?: boolean
}

export default function ReactPlayerAdapter({
  src,
  poster,           // Mux thumbnail URL from video JSON
  ['data-playing']: dataPlaying,
  ['data-loop']: dataLoop,
  ['data-muted']: dataMuted,
  ...rest
}: AdapterProps) {
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [isActuallyPlaying, setIsActuallyPlaying] = useState(false)
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false)
  const [shouldPreload, setShouldPreload] = useState(false)
  const [userClicked, setUserClicked] = useState(false)
  const [hasCreatedPlayer, setHasCreatedPlayer] = useState(false)
  const [hasStartedMounting, setHasStartedMounting] = useState(false) // Track if we've ever started mounting visible player
  const playerRef = useRef<any>(null)
  const preloadStartedRef = useRef(false)
  const hasEverLoadedRef = useRef(false)
  const consecutiveTimeUpdatesRef = useRef(0)
  const lastPlaybackTimeRef = useRef(0)
  const hasEverPlayedSuccessfullyRef = useRef(false)
  const hasEverHadAutoplayRef = useRef(false) // Track if we've ever had autoplay enabled
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null) // Debounce rapid shouldPlay changes
  const playPromiseRef = useRef<Promise<void> | null>(null) // Track pending play promise
  const shouldPauseWhenPromiseResolvesRef = useRef<boolean>(false) // Track if we still want to pause when promise resolves
  const currentShouldPlayRef = useRef<boolean>(false) // Track current shouldPlay value for promise resolution checks
  const [debouncedShouldPlay, setDebouncedShouldPlay] = useState(false)
  const shouldAutoplay = !!dataPlaying
  
  // Suppress "play() interrupted by pause()" errors globally
  // This is expected behavior when scrolling quickly between videos
  useEffect(() => {
    const originalError = console.error
    const errorHandler = (event: ErrorEvent) => {
      if (event.message && event.message.includes('play()') && event.message.includes('pause()')) {
        event.preventDefault()
        return false
      }
    }
    
    window.addEventListener('error', errorHandler)
    
    // Also suppress unhandled promise rejections for AbortError
    const rejectionHandler = (event: PromiseRejectionEvent) => {
      if (event.reason && event.reason.name === 'AbortError' && 
          event.reason.message && event.reason.message.includes('play()')) {
        event.preventDefault()
        return false
      }
    }
    
    window.addEventListener('unhandledrejection', rejectionHandler)
    
    return () => {
      window.removeEventListener('error', errorHandler)
      window.removeEventListener('unhandledrejection', rejectionHandler)
    }
  }, [])
  
  // Track when autoplay becomes enabled (even if it flickers)
  useEffect(() => {
    if (shouldAutoplay) {
      hasEverHadAutoplayRef.current = true
      // Ensure we start mounting immediately when autoplay is first observed
      if (!hasStartedMounting) {
        setHasStartedMounting(true)
      }
    }
  }, [shouldAutoplay, hasStartedMounting])
  
  // Memoize rest props to prevent ReactPlayer from re-rendering due to new callback references
  const stableRestProps = useMemo(() => {
    const { onPlaying, onPause, ...otherRest } = rest as any
    return otherRest
  }, [rest])
  
  // Start preloading when thumbnail is visible (only once per component instance)
  useEffect(() => {
    // Start preloading immediately when thumbnail is shown, but only once
    if (poster && !preloadStartedRef.current) {
      preloadStartedRef.current = true
      setShouldPreload(true)
    }
  }, [poster])
  
  // Handle video ready state - memoized to prevent re-renders
  // This is for the hidden preload player - don't mark as "ever loaded" yet
  const handleReady = useCallback(() => {
    setIsVideoReady(true)
    // If we've ever had autoplay enabled, start mounting immediately when video is ready
    // This ensures we mount even if dataPlaying flickered to false
    if (hasEverHadAutoplayRef.current) {
      setHasStartedMounting(true)
    }
    // Don't set hasEverLoadedRef here - only set it when visible player is ready
  }, [])
  
  // Handle user click to play
  const handleThumbnailClick = () => {
    setUserClicked(true)
    setShouldPreload(true)
    // If player already exists, just play it
    if (hasCreatedPlayer && playerRef.current) {
      const internalPlayer = (playerRef.current as any).getInternalPlayer?.()
      if (internalPlayer && internalPlayer instanceof HTMLVideoElement) {
        internalPlayer.play()
      }
    }
  }
  
  // Track when we start mounting to prevent unmounting due to dataPlaying flicker
  // Set hasStartedMounting immediately when video is ready and we've ever had autoplay
  useEffect(() => {
    if (isVideoReady && hasEverHadAutoplayRef.current && !hasStartedMounting) {
      setHasStartedMounting(true)
    }
  }, [isVideoReady, hasStartedMounting])
  
  // Mount visible player once created, keep it mounted permanently
  // Mount if: user clicked OR player already created OR we've started mounting before OR 
  // (we've ever had autoplay enabled - mount immediately to let it load) OR
  // (video ready AND autoplay currently enabled)
  // This ensures we mount even if dataPlaying flickers between true/false
  const shouldMountPlayer = hasCreatedPlayer || userClicked || hasStartedMounting || hasEverHadAutoplayRef.current || (isVideoReady && shouldAutoplay)
  
  // Control playback state (not mounting)
  // Play if: (autoplay currently enabled OR user clicked) AND 
  // (player is created OR we've ever had autoplay - let visible player load and play)
  // Respect dataPlaying state: pause when video leaves viewport (shouldAutoplay becomes false)
  const shouldPlay = (shouldAutoplay || userClicked) && 
                     (hasCreatedPlayer || hasEverHadAutoplayRef.current)
  
  // Update ref to track current shouldPlay value for promise resolution checks
  useEffect(() => {
    currentShouldPlayRef.current = shouldPlay
  }, [shouldPlay])
  
  // Debounce shouldPlay changes to prevent rapid play/pause toggles that cause "play() interrupted by pause()" errors
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    
    // For play: only apply if video is ready, otherwise wait
    // For pause: wait for play promise to resolve or add delay
    if (shouldPlay) {
      // Cancel any pending pause and clear pause intent
      shouldPauseWhenPromiseResolvesRef.current = false
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = null
      }
      
      // Check if video is ready before trying to play
      if (hasCreatedPlayer && playerRef.current) {
        const internalPlayer = (playerRef.current as any).getInternalPlayer?.()
        if (internalPlayer && internalPlayer instanceof HTMLVideoElement) {
          const video = internalPlayer as HTMLVideoElement
          // Only play if video has loaded enough data (readyState >= 2 = HAVE_CURRENT_DATA)
          if (video.readyState >= 2) {
            // Create play promise immediately when we're about to play
            // This prevents pause() from being called before play() resolves
            playPromiseRef.current = new Promise<void>((resolve) => {
              setTimeout(() => {
                playPromiseRef.current = null
                resolve()
              }, 600)
            })
            setDebouncedShouldPlay(true)
          } else {
            // Video not ready yet, wait a bit and try again
            debounceTimeoutRef.current = setTimeout(() => {
              if (shouldPlay && video.readyState >= 2) {
                setDebouncedShouldPlay(true)
              }
            }, 100)
          }
        } else {
        // Player not ready yet, apply immediately (ReactPlayer will handle it)
        // Create play promise immediately
        playPromiseRef.current = new Promise<void>((resolve) => {
          setTimeout(() => {
            playPromiseRef.current = null
            resolve()
          }, 600)
        })
        setDebouncedShouldPlay(true)
      }
    } else {
      // Player not created yet, apply immediately (ReactPlayer will handle it)
      // Create play promise immediately
      playPromiseRef.current = new Promise<void>((resolve) => {
        setTimeout(() => {
          playPromiseRef.current = null
          resolve()
        }, 600)
      })
      setDebouncedShouldPlay(true)
      }
    } else {
      // If there's a pending play promise, wait for it to resolve before pausing
      // Check the ref directly (not in the log data) to avoid stale closure issues
      if (playPromiseRef.current) {
        // Mark that we want to pause when the promise resolves
        shouldPauseWhenPromiseResolvesRef.current = true
        playPromiseRef.current
          .then(() => {
            // Add a small delay to ensure play() has had time to complete
            // The synthetic promise resolves after 600ms, but the actual play() might need a bit more time
            setTimeout(() => {
              // Check both the ref AND the current shouldPlay state
              // The ref might be stale if shouldPlay changed back to true
              const stillWantToPause = shouldPauseWhenPromiseResolvesRef.current
              const currentShouldPlay = currentShouldPlayRef.current
              shouldPauseWhenPromiseResolvesRef.current = false
              
              // Use a callback to check the current debouncedShouldPlay state
              // This ensures we're checking the most up-to-date state
              setDebouncedShouldPlay((currentDebounced) => {
                // Only pause if:
                // 1. We still want to pause (ref is true)
                // 2. shouldPlay is currently false
                // 3. debouncedShouldPlay is currently true (video is playing)
                // If debouncedShouldPlay is already false, we don't need to pause
                // If shouldPlay changed back to true, we should not pause
                if (stillWantToPause && !currentShouldPlay && currentDebounced) {
                  return false
                } else {
                  return currentDebounced // Keep current state
                }
              })
            }, 100) // Small delay to ensure play() has completed
            playPromiseRef.current = null
          })
          .catch(() => {
            // Check if we still want to pause
            const stillWantToPause = shouldPauseWhenPromiseResolvesRef.current
            const currentShouldPlay = currentShouldPlayRef.current
            shouldPauseWhenPromiseResolvesRef.current = false
            // Only pause if we still want to pause AND shouldPlay is currently false
            if (stillWantToPause && !currentShouldPlay) {
              // Play failed or was interrupted, safe to pause now
              setDebouncedShouldPlay(false)
            }
            playPromiseRef.current = null
          })
      } else {
        // No pending play promise, but onPlay might fire soon, so add a delay
        // This gives onPlay time to create the promise if it hasn't fired yet
        debounceTimeoutRef.current = setTimeout(() => {
          // Check again if promise was created in the meantime
          if (playPromiseRef.current) {
            playPromiseRef.current
              .then(() => {
                setDebouncedShouldPlay(false)
                playPromiseRef.current = null
              })
              .catch(() => {
                setDebouncedShouldPlay(false)
                playPromiseRef.current = null
              })
          } else {
            setDebouncedShouldPlay(false)
          }
        }, 100) // Short delay to let onPlay fire and create promise
      }
    }
    
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [shouldPlay, hasCreatedPlayer])
  
  if (!src) {
    // while next-video processes, src can be temporarily null in dev
    return <div style={{ width: '100%', aspectRatio: '16 / 9', background: '#000' }} />
  }

  return (
    <>
      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
      <div style={{ width: '100%', aspectRatio: '16 / 9', position: 'relative' }}>
      {/* Hidden ReactPlayer for preloading - only until visible player is created */}
      {/* Remove hidden player once visible player is ready to avoid duplicate requests */}
      {shouldPreload && !hasCreatedPlayer && (
        <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', zIndex: -1, width: '1px', height: '1px', overflow: 'hidden' }}>
          <ReactPlayer
            key="preload-player" // Stable key to prevent remounting
            src={src}
            width="1px"
            height="1px"
            playing={false}
            muted={true}
            playsInline
            onReady={handleReady}
            {...stableRestProps}
          />
        </div>
      )}
      
      {/* Thumbnail - always mounted when poster exists, visibility controlled by opacity */}
      {poster && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            cursor: shouldPlay ? 'default' : 'pointer',
            zIndex: 20,
            opacity: isActuallyPlaying ? 0 : 1,
            transition: 'opacity 400ms ease-out',
            background: '#000',
            pointerEvents: isActuallyPlaying ? 'none' : (shouldPlay ? 'none' : 'auto')
          }}
          onClick={handleThumbnailClick}
          className="group"
        >
          {!thumbnailLoaded && (
            <div className="absolute inset-0 bg-gray-800 animate-pulse" />
          )}
          <Image
            src={poster}
            alt="Video thumbnail"
            fill
            style={{ objectFit: 'cover' }}
            unoptimized
            loading="lazy"
            onLoad={() => setThumbnailLoaded(true)}
            fetchPriority={shouldAutoplay ? "high" : "low"}
          />
          {/* Play button overlay - only show if not autoplaying */}
          {!shouldAutoplay && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 flex items-center justify-center group-hover:bg-white transition-colors">
                <svg 
                  className="w-8 h-8 md:w-10 md:h-10 text-gray-900 ml-1" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
          )}
          {/* Shimmer effect when video is preloading but not loaded yet */}
          {shouldPreload && !hasEverLoadedRef.current && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div 
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
                  width: '50%',
                  height: '100%',
                  animation: 'shimmer 2s ease-in-out infinite',
                  transform: 'skewX(-20deg)',
                }}
              />
            </div>
          )}
        </div>
      )}
      
      {/* Visible video player - mount once, keep mounted permanently */}
      {shouldMountPlayer && (
        <div style={{
          position: 'relative',
          zIndex: 10,
          opacity: 1,
        }}>
      <ReactPlayer
            ref={playerRef}
            key="visible-player" // Stable key to prevent remounting
        src={src}
        width="100%"
        height="100%"
            playing={debouncedShouldPlay}
        loop={!!dataLoop}
        muted={dataMuted ?? true}
        controls
        playsInline
            onReady={() => {
              if (!hasCreatedPlayer) {
                setHasCreatedPlayer(true)
                hasEverLoadedRef.current = true
              }
            }}
            onPlay={() => {
              consecutiveTimeUpdatesRef.current = 0
              lastPlaybackTimeRef.current = 0
              
              // Only create a promise if one doesn't already exist
              // The promise should have been created synchronously when we set debouncedShouldPlay to true
              // If it doesn't exist, create it now as a fallback
              if (!playPromiseRef.current) {
                playPromiseRef.current = new Promise<void>((resolve) => {
                  setTimeout(() => {
                    playPromiseRef.current = null
                    resolve()
                  }, 600) // Give play() 600ms to resolve
                })
              }
            }}
            onStart={() => {
              // Video started
            }}
            onPlaying={() => {
              // onPlaying fires when video is actually playing (not buffering)
              if (!hasEverPlayedSuccessfullyRef.current) {
                hasEverPlayedSuccessfullyRef.current = true
                setIsActuallyPlaying(true)
              }
            }}
            onProgress={(state: any) => {
              // Backup detection: Only count when time is actually advancing past 0
              if (hasEverPlayedSuccessfullyRef.current) {
                return
              }

              const currentTime = state.playedSeconds || 0

              if (currentTime > 0 && currentTime > lastPlaybackTimeRef.current) {
                consecutiveTimeUpdatesRef.current++
                lastPlaybackTimeRef.current = currentTime

                // After 2 consecutive time advances, video is playing
                if (consecutiveTimeUpdatesRef.current >= 2) {
                  hasEverPlayedSuccessfullyRef.current = true
                  setIsActuallyPlaying(true)
                }
              }
            }}
            onPause={() => {
              // Clear play promise ref when paused
              playPromiseRef.current = null
              // Only reset if video paused before ever playing successfully
              if (!shouldPlay && !hasEverPlayedSuccessfullyRef.current) {
                // Video paused before ever playing successfully - reset tracking
                consecutiveTimeUpdatesRef.current = 0
                lastPlaybackTimeRef.current = 0
              }
              // If already played successfully, keep thumbnail hidden on pause
            }}
            onError={(error: any) => {
              // Silently handle "play() interrupted by pause()" errors - they're expected
              if (error?.message?.includes('play()') && error?.message?.includes('pause()')) {
                // This is expected when scrolling quickly, ignore it
                return
              }
              // Log other errors for debugging
              console.error('ReactPlayer error:', error)
            }}
            // Suppress AbortError from play() being interrupted by pause()
            // This is expected behavior when scrolling quickly between videos
            onAbort={() => {
              // Silently handle abort - this is expected when play() is interrupted
            }}
            // Use stable rest props to prevent re-renders
            {...stableRestProps}
      />
    </div>
      )}
      </div>
    </>
  )
}
